import { FileText, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader } from '../_primitives';
import type { MetadataFieldState } from '../postgenEvidence';
import { MetadataFieldChip } from './MetadataFieldChip';
import { AiSuggestButton } from './AiSuggestButton';

/** Soft guidance length for the excerpt (roughly a meta-description worth of text). */
const EXCERPT_GUIDANCE = 155;

export interface SummaryCardProps {
  /** Current excerpt override value (controlled). Empty = use the derived excerpt. */
  excerpt: string;
  /**
   * Derived fallback excerpt (first paragraph / meta description) shown as the
   * placeholder when the override is empty.
   */
  derivedExcerpt: string;
  onExcerptChange: (value: string) => void;
  /** Read-only key takeaways extracted from the article's key-takeaways callout block. */
  takeaways: string[];
  /**
   * Jump to the takeaways block inside the editor. The block is body content and
   * is edited there, not in this card.
   */
  onJumpToTakeaways?: () => void;
  /**
   * Optional per-field provenance state (E0 evidence) keyed by metadataPatch
   * field name. The mount supplies this; absent → no chips render.
   */
  fieldStates?: Record<string, MetadataFieldState>;
  /** Content ID for the per-field suggest endpoint. */
  contentId?: string;
  /** Tenant ID for the per-field suggest endpoint. */
  tenantId?: string;
  /** Disable suggest while whole-run generation is in flight. */
  metadataGenerating?: boolean;
}

function SoftCharCounter({ current, max }: { current: number; max: number }) {
  const isOver = current > max;
  return (
    <span
      className={cn(
        'font-mono text-[10.5px] font-semibold',
        isOver ? 'text-destructive' : 'text-muted-foreground',
      )}
      aria-live="polite"
    >
      {current}/{max}
    </span>
  );
}

/**
 * Pure presentational rail card for Family C (summaries): the editable post
 * excerpt plus a read-only view of the article's key takeaways. No internal
 * state, effects, or service calls.
 */
export function SummaryCard({
  excerpt,
  derivedExcerpt,
  onExcerptChange,
  takeaways,
  onJumpToTakeaways,
  fieldStates,
  contentId,
  tenantId,
  metadataGenerating,
}: SummaryCardProps) {
  const hasTakeaways = takeaways.length > 0;
  const excerptPlaceholder =
    derivedExcerpt || 'A short summary shown in listings and search results';

  return (
    <Card railSection="summary">
      <CardHeader icon={<FileText className="size-3.5" />} title="Summary" />

      <div className="px-3 pb-3 flex flex-col gap-3">
        {/* Excerpt */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
                Excerpt
              </span>
              <MetadataFieldChip state={fieldStates?.excerpt} />
              {contentId && tenantId && (
                <AiSuggestButton
                  field="excerpt"
                  onSuggest={onExcerptChange}
                  currentValue={excerpt}
                  contentId={contentId}
                  tenantId={tenantId}
                  disabled={metadataGenerating}
                />
              )}
            </span>
            <SoftCharCounter current={excerpt.length} max={EXCERPT_GUIDANCE} />
          </div>
          <textarea
            value={excerpt}
            onChange={(e) => onExcerptChange(e.target.value)}
            placeholder={excerptPlaceholder}
            rows={3}
            className="w-full px-2.5 py-1.5 text-[12.5px] border border-border rounded-md bg-card outline-none transition-shadow resize-y focus:border-coral focus:ring-2 focus:ring-coral/20"
          />
          <p className="text-[10.5px] text-muted-foreground mt-1">
            Leave empty to use the automatic excerpt (from the meta description).
          </p>
        </div>

        {/* Key takeaways */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider">
              Key takeaways
            </span>
            {hasTakeaways && onJumpToTakeaways && (
              <button
                type="button"
                onClick={onJumpToTakeaways}
                className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-coral hover:text-coral-hover cursor-pointer bg-transparent border-0"
              >
                <Pencil className="size-3" />
                Edit in article
              </button>
            )}
          </div>

          {hasTakeaways ? (
            <ul className="flex flex-col gap-1.5">
              {takeaways.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[12px] text-foreground leading-snug"
                >
                  <span
                    className="mt-[6px] size-1.5 rounded-full bg-teal shrink-0"
                    aria-hidden
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center text-[12px] text-muted-foreground py-2">
              No key takeaways in this article.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
